import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { observer } from 'mobx-react';
import { FilterChips as OleFilterChips } from '@ole-ui/ole-ui-components';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import useMobxStores from 'hooks/useMobxStores';
import { useTranslation } from 'react-i18next';

export type FilterChipsProps = {
  /**
   * Must match whichever store backs the sidebar filters on the current page.
   * Catalog uses `catalogStore`; Skills paths uses `learningPathStore`.
   * Do not derive this from NewCatalog — that flag broke chips when it pointed
   * at the wrong store while the sidebar still updated the other one.
   */
  filterStore?: 'catalog' | 'learningPath';
};

const FilterChips = ({ filterStore = 'catalog' }: FilterChipsProps) => {
  const { catalogStore, learningPathStore } = useMobxStores();
  const { t } = useTranslation();

  const store =
    filterStore === 'learningPath' ? learningPathStore : catalogStore;

  const filterConfigs = store.getFilterConfigs();
  const groups = filterConfigs.map((filterConfig) => ({
    title: filterConfig.title ?? '',
    groupKey: filterConfig.key,
    options:
      store.filters[filterConfig.key]?.map((option) => ({
        key: option.key,
        title: option.value,
        count: option.count,
        checked: option.checked,
      })) ?? [],
    mergeIdenticalLabels: filterConfig.groupIdenticalLabels,
  }));

  const handleChipClick = (groupKey: string, filterKeys: string | string[]) => {
    store.toggleFilterOption(groupKey, filterKeys);
    store.recalculateFilterCounts();
  };

  return (
    <OleFilterChips
      className="filters-chipgroup"
      chipClassName="filters-chipgroup-chip"
      filteredCount={store.filteredCount}
      resultsLabel={t('Results')}
      resultsTitle={t('The number of results found for the current filters')}
      groups={groups}
      onChipClick={handleChipClick}
    />
  );
};

export default withErrorBoundary(
  observer(FilterChips),
  ComponentError,
  handleError,
);
